import {
  ReportBuilderDataSourceId,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import { FactTables } from '../../models/fact-tables';
import { toMeasureBuilderData } from '../../models/measure-properties';
import { ReportBuilderColumns } from '../../report-builder-columns';
import { ChartFilterDefinition } from '../charts/chart-filter-definition';
import { ReportBuilderCharts } from '../charts/report-builder-charts';
import { ReportBuilderCommonCharts } from '../charts/report-builder-common-charts';
import { ReportBuilderDataSourceInstance } from '../report-builder-data-source-instance';
import { ReportBuilderGroups } from '../report-builder-groups';
import { ReportBuilderScopeDataAccessors } from '../report-builder-scope-data-accessors';

export class AccountCreditsDataSource extends ReportBuilderDataSourceInstance {
  override readonly id = ReportBuilderDataSourceId.AccountCredits;
  override readonly name = 'Account Credits';
  override readonly description =
    'One result per Account Credit within Principle. This includes information about the patient it was allocated to and the invoice it was taken from.';
  override readonly factTable = FactTables.accountCreditEvent.latestEvent;
  override readonly rowItemName = 'Account Credit';

  override readonly queryableTimestamps = [
    { property: this.factTable.accountCredit.createdAt },
  ];

  scopes = [TemplateScope.Patient];
  scopeDataAccessor = ReportBuilderScopeDataAccessors.build([
    this.factTable.patient,
  ]);

  override readonly groupByOptions = [
    ReportBuilderGroups.toGroup('Common', [
      ReportBuilderGroups.toOption(
        this.factTable.accountCredit.reservedForPractitioner
      ),
      ReportBuilderGroups.toOption(this.factTable.practice.name),
      ReportBuilderGroups.toOption(this.factTable.brand.name),
      ReportBuilderGroups.toOption(this.factTable.count),
    ]),
    ReportBuilderGroups.toGroup('Account Credit', [
      ...ReportBuilderGroups.accountCredit(this.factTable.accountCredit)
        .options,
      ReportBuilderGroups.toOption(this.factTable.treatmentCategory),
    ]),
    ReportBuilderGroups.invoice(this.factTable.invoice),
    ReportBuilderGroups.patient(this.factTable.patient),
  ];

  override readonly availableColumns = [
    ReportBuilderColumns.toColumnGroup('Account Credit', [
      this.factTable.remaining,
      this.factTable.hasRemaining,
      ...ReportBuilderColumns.accountCredit(this.factTable.accountCredit),
      this.factTable.treatmentCategory,
    ]),
    ReportBuilderColumns.toColumnGroup('Invoice', [
      ...ReportBuilderColumns.invoice(this.factTable.invoice),
      this.factTable.invoiceLink,
    ]),
    ReportBuilderColumns.toColumnGroup('Patient', [
      ...ReportBuilderColumns.patient(this.factTable.patient),
      this.factTable.patientLink,
    ]),
    ReportBuilderColumns.toColumnGroup('Practice', [
      this.factTable.practice.name,
    ]),
  ];

  override readonly defaultColumns = ReportBuilderColumns.toReferences([
    this.factTable.patient.name,
    this.factTable.accountCredit.reservedForPractitioner,
    this.factTable.treatmentCategory,
    this.factTable.accountCredit.createdAt,
    this.factTable.invoiceLink,
    this.factTable.accountCredit.amount,
    this.factTable.accountCredit.used,
    this.factTable.remaining,
  ]);

  override readonly availableCharts = [
    ReportBuilderCharts.predefined(
      'overview.brand.price',
      this.factTable.brand.name,
      ChartFilterDefinition.numberSummary(
        'Total Price',
        this.factTable.brand.name,
        [toMeasureBuilderData(this.factTable.accountCredit.amount)]
      )
    ),
    ReportBuilderCharts.predefined(
      'overview.practice',
      this.factTable.practice.name,
      ChartFilterDefinition.pie('Practice', this.factTable.practice.name, [
        toMeasureBuilderData(this.factTable.accountCredit.amount),
      ])
    ),
    ReportBuilderCharts.predefined(
      'overview.totalAccountCredits',
      this.factTable.accountCredit.createdAt,
      ChartFilterDefinition.columnOverTime(
        'Total Account Credits',
        this.factTable.accountCredit.createdAt,
        [toMeasureBuilderData(this.factTable.accountCredit.amount)],
        { rotateXAxisLabels: true }
      )
    ),
    ...ReportBuilderCommonCharts.patient(
      this.factTable.patient,
      this.factTable.accountCredit.amount
    ),
    ...ReportBuilderCommonCharts.invoice(
      this.factTable.invoice,
      this.factTable.accountCredit.amount
    ).filter(
      (chart) =>
        ![
          'invoice.productsTotal',
          'invoice.treatmentsTotal',
          'invoice.products',
          'invoice.treatments',
          'invoice.serviceCodes',
        ].includes(chart.id)
    ),
    ReportBuilderCharts.predefined(
      'accountCredit.total',
      this.factTable.brand.name,
      ChartFilterDefinition.numberSummary(
        'Total Amount',
        this.factTable.brand.name,
        [toMeasureBuilderData(this.factTable.accountCredit.amount)]
      )
    ),
    ReportBuilderCharts.predefined(
      'accountCredit.totalUsed',
      this.factTable.brand.name,
      ChartFilterDefinition.numberSummary(
        'Total Used',
        this.factTable.brand.name,
        [toMeasureBuilderData(this.factTable.accountCredit.used)]
      )
    ),
    ReportBuilderCharts.predefined(
      'accountCredit.totalRemaining',
      this.factTable.brand.name,
      ChartFilterDefinition.numberSummary(
        'Total Remaining',
        this.factTable.brand.name,
        [toMeasureBuilderData(this.factTable.remaining)]
      )
    ),
    ...ReportBuilderCommonCharts.accountCredit(
      FactTables.accountCreditEvent.latestEvent.accountCredit,
      this.factTable.accountCredit.amount
    ),
    ReportBuilderCharts.predefined(
      'accountCredit.treatmentCategory',
      this.factTable.treatmentCategory,
      ChartFilterDefinition.column(
        'Treatment Category',
        this.factTable.treatmentCategory,
        [toMeasureBuilderData(this.factTable.accountCredit.amount)],
        { rotateXAxisLabels: true },
        { groupLimit: 15, otherGroupLabel: 'Other', colourOverride: true }
      )
    ),
  ];
}
